"use client";

// ===================================
// ENHANCED SETTINGS PAGE WITH AI PREFERENCES
// src/pages/Settings.tsx
// PRESERVES: All existing components (InterfaceSettings, PublicAccessCodeManager, CreateChatbot)
// ADDS: AI Preferences, Business AI Configuration (admin), Tabbed Navigation
// ===================================

import React, { useState, useEffect } from "react";
import CreateChatbot from "../components/CreateChatbot";
import Header from "../components/Header";
import InterfaceSettings from "../components/InterfaceSettings";
import { useAuth } from "../contexts/AuthContext";
import { useAIConfig } from "../contexts/AIConfigContext";
import PublicAccessCodeManager from "../components/PublicAccessCodeManager";
import { aiConfigApi } from "../services/aiConfigApi";
import { notifyError, notifySuccess } from "../utils/Toastify";

import {
  Settings as SettingsIcon,
  Sparkles,
  Shield,
  Bot,
  Cpu,
  RefreshCw,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Key,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Building2,
  User,
  Loader2,
  Zap,
} from "lucide-react";

// ===================================
// TYPES
// ===================================
type SettingsTab = "interface" | "ai" | "access" | "chatbot";

interface BusinessAIConfig {
  default_llm_provider: string;
  default_llm_model: string;
  default_embedding_provider: string;
  default_embedding_model: string;
  default_mode: string;
  fallback_llm_provider: string;
  fallback_llm_model: string;
  allow_user_override: boolean;
  allow_user_api_keys: boolean;
}

interface UserAIPreferences {
  preferred_llm_provider?: string;
  preferred_llm_model?: string;
  preferred_mode?: string;
  has_custom_api_key?: boolean;
}

// ===================================
// AI PREFERENCES COMPONENT
// ===================================
const AIPreferences: React.FC = () => {
  const {
    effectiveConfig,
    modes,
    llmProviders,
    isLoading: isContextLoading,
    refreshConfig,
  } = useAIConfig();

  const [userPrefs, setUserPrefs] = useState<UserAIPreferences | null>(null);
  const [businessAllowsOverride, setBusinessAllowsOverride] = useState(true);
  const [businessAllowsApiKeys, setBusinessAllowsApiKeys] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Form state
  const [selectedMode, setSelectedMode] = useState<string>("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [apiKey, setApiKey] = useState<string>("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load user preferences
  useEffect(() => {
    loadUserPreferences();
  }, []);

  // Track changes
  useEffect(() => {
    if (!userPrefs) return;
    
    const changed = 
      selectedMode !== (userPrefs.preferred_mode || "") ||
      selectedProvider !== (userPrefs.preferred_llm_provider || "") ||
      selectedModel !== (userPrefs.preferred_llm_model || "") ||
      apiKey.length > 0;
    
    setHasChanges(changed);
  }, [selectedMode, selectedProvider, selectedModel, apiKey, userPrefs]);

  const loadUserPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await aiConfigApi.getUserPreferences();
      const data = response.data;
      
      setUserPrefs(data.preferences || {});
      setBusinessAllowsOverride(data.business_allows_override ?? true);
      setBusinessAllowsApiKeys(data.business_allows_api_keys ?? true);
      
      // Set form state from preferences
      setSelectedMode(data.preferences?.preferred_mode || "");
      setSelectedProvider(data.preferences?.preferred_llm_provider || "");
      setSelectedModel(data.preferences?.preferred_llm_model || "");
    } catch (error) {
      console.error("Failed to load user preferences:", error);
      notifyError("Failed to load AI preferences");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      const updateData: any = {};
      if (selectedMode) updateData.preferred_mode = selectedMode;
      if (selectedProvider) updateData.preferred_llm_provider = selectedProvider;
      if (selectedModel) updateData.preferred_llm_model = selectedModel;
      if (apiKey) updateData.openai_api_key = apiKey;

      await aiConfigApi.updateUserPreferences(updateData);
      await refreshConfig();
      await loadUserPreferences();
      
      setApiKey(""); // Clear API key field after save
      notifySuccess("AI preferences saved successfully");
    } catch (error: any) {
      console.error("Failed to save preferences:", error);
      notifyError(error.response?.data?.error?.message || "Failed to save preferences");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setIsSaving(true);
      
    await aiConfigApi.updateUserPreferences({
  preferred_mode: undefined,      // ✅ undefined is assignable to string | undefined
  preferred_llm_provider: undefined,
  preferred_llm_model: undefined,
});
      
      await refreshConfig();
      await loadUserPreferences();
      
      setSelectedMode("");
      setSelectedProvider("");
      setSelectedModel("");
      
      notifySuccess("Preferences reset to defaults");
    } catch (error) {
      notifyError("Failed to reset preferences");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveApiKey = async () => {
    if (!window.confirm("Are you sure you want to remove your custom API key?")) {
      return;
    }
    
    try {
      setIsSaving(true);
      await aiConfigApi.removeApiKey();
      await loadUserPreferences();
      notifySuccess("API key removed");
    } catch (error) {
      notifyError("Failed to remove API key");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestProvider = async () => {
    if (!selectedProvider) {
      notifyError("Please select a provider to test");
      return;
    }

    try {
      setIsTesting(true);
      setTestResult(null);
      
      const response = await aiConfigApi.testLLM({
        provider: selectedProvider,
        model: selectedModel || undefined,
        api_key: apiKey || undefined,
      });
      
      setTestResult({
        success: response.data.success,
        message: response.data.success 
          ? `Connection successful (${response.data.response_time_ms}ms)`
          : response.data.error || "Connection failed"
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || "Connection test failed"
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Get available models for selected provider
  const getModelsForProvider = (providerKey: string) => {
    const provider = llmProviders?.find(p => p.provider_key === providerKey);
    return provider?.config?.models || [];
  };

  if (isLoading || isContextLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#8b3dff] animate-spin" />
        <span className="ml-3 text-gray-400">Loading AI preferences...</span>
      </div>
    );
  }

  if (!businessAllowsOverride) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <h3 className="text-amber-100 font-medium">User Overrides Disabled</h3>
            <p className="text-amber-200/70 text-sm mt-1">
              Your organization has disabled user-level AI preference overrides.
              Contact your administrator if you need custom settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#8b3dff]" />
            AI Preferences
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Customize how AI responds to your messages
          </p>
        </div>
        
        {/* Current Config Badge */}
        {effectiveConfig && (
          <div className="text-right text-xs text-gray-500">
            <div>Current: {effectiveConfig.llm_provider}</div>
            <div className="capitalize">{effectiveConfig.source.replace('_', ' ')}</div>
          </div>
        )}
      </div>

      {/* Mode Selection */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Default Operation Mode
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(modes || []).map((mode) => (
            <button
              key={mode.key}
              onClick={() => setSelectedMode(mode.key)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                selectedMode === mode.key
                  ? "bg-[#8b3dff]/20 border-[#8b3dff] text-white"
                  : "bg-[#0f172a]/50 border-[#334155] text-gray-400 hover:border-[#8b3dff]/50 hover:text-white"
              }`}
            >
              <span className="text-2xl">{mode.icon}</span>
              <span className="text-sm font-medium">{mode.displayName}</span>
              <span className="text-xs text-gray-500 text-center line-clamp-2">
                {mode.description}
              </span>
            </button>
          ))}
        </div>
        {selectedMode && (
          <p className="mt-3 text-xs text-gray-500">
            Selected: {modes?.find(m => m.key === selectedMode)?.description}
          </p>
        )}
      </div>

      {/* Provider Selection */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Preferred AI Provider
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(llmProviders || []).map((provider) => (
            <button
              key={provider.provider_key}
              onClick={() => {
                setSelectedProvider(provider.provider_key);
                setSelectedModel(provider.config?.default_model || "");
              }}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                selectedProvider === provider.provider_key
                  ? "bg-[#8b3dff]/20 border-[#8b3dff] text-white"
                  : "bg-[#0f172a]/50 border-[#334155] text-gray-400 hover:border-[#8b3dff]/50 hover:text-white"
              }`}
            >
              <Cpu className={`w-5 h-5 ${
                selectedProvider === provider.provider_key ? "text-[#8b3dff]" : ""
              }`} />
              <div className="text-left">
                <div className="font-medium">{provider.display_name}</div>
                <div className="text-xs text-gray-500">
                  {provider.config?.models?.length || 0} models available
                </div>
              </div>
              {selectedProvider === provider.provider_key && (
                <Check className="w-4 h-4 text-[#8b3dff] ml-auto" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Model Selection (when provider is selected) */}
      {selectedProvider && (
        <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Preferred Model
          </label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
          >
            <option value="">Use provider default</option>
            {getModelsForProvider(selectedProvider).map((model: string) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* API Key Section (if allowed) */}
      {businessAllowsApiKeys && selectedProvider === "openai" && (
        <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
              <Key className="w-4 h-4" />
              OpenAI API Key
            </label>
            {userPrefs?.has_custom_api_key && (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Custom key configured
              </span>
            )}
          </div>
          
          <div className="relative">
            <input
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={userPrefs?.has_custom_api_key ? "••••••••••••••••" : "sk-..."}
              className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 pr-20 text-white placeholder-gray-500 focus:border-[#8b3dff] focus:outline-none"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="p-2 text-gray-500 hover:text-white transition-colors"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          
          <p className="mt-2 text-xs text-gray-500">
            Use your own OpenAI API key for direct billing. Leave empty to use organization's key.
          </p>
          
          {userPrefs?.has_custom_api_key && (
            <button
              onClick={handleRemoveApiKey}
              disabled={isSaving}
              className="mt-3 text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              Remove custom API key
            </button>
          )}
        </div>
      )}

      {/* Test Connection */}
      {selectedProvider && (
        <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-300">Test Connection</h4>
              <p className="text-xs text-gray-500 mt-1">
                Verify your configuration works correctly
              </p>
            </div>
            <button
              onClick={handleTestProvider}
              disabled={isTesting}
              className="flex items-center gap-2 px-4 py-2 bg-[#334155] hover:bg-[#475569] text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isTesting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              Test
            </button>
          </div>
          
          {testResult && (
            <div className={`mt-4 p-3 rounded-lg ${
              testResult.success 
                ? "bg-emerald-500/10 border border-emerald-500/30" 
                : "bg-red-500/10 border border-red-500/30"
            }`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-400" />
                )}
                <span className={testResult.success ? "text-emerald-300" : "text-red-300"}>
                  {testResult.message}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-[#334155]/50">
        <button
          onClick={handleReset}
          disabled={isSaving}
          className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" />
          Reset to Defaults
        </button>
        
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8b3dff] to-[#6b2fd4] hover:from-[#7a35e6] hover:to-[#5c28bf] text-white rounded-xl font-medium shadow-lg shadow-[#8b3dff]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Save Preferences
        </button>
      </div>
    </div>
  );
};

// ===================================
// BUSINESS AI CONFIGURATION (ADMIN ONLY)
// ===================================
const BusinessAIConfiguration: React.FC = () => {
  const { modes, llmProviders, embeddingProviders, refreshConfig } = useAIConfig();
  
  const [config, setConfig] = useState<BusinessAIConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadBusinessConfig();
  }, []);

  const loadBusinessConfig = async () => {
    try {
      setIsLoading(true);
      const response = await aiConfigApi.getBusinessConfig();
      setConfig(response.data.config);
    } catch (error) {
      console.error("Failed to load business config:", error);
      notifyError("Failed to load business AI configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    
    try {
      setIsSaving(true);
      await aiConfigApi.updateBusinessConfig(config);
      await refreshConfig();
      notifySuccess("Business AI configuration saved");
    } catch (error: any) {
      notifyError(error.response?.data?.error?.message || "Failed to save configuration");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#8b3dff] animate-spin" />
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#8b3dff]" />
          Business AI Configuration
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Set default AI settings for all users in your organization
        </p>
      </div>

      {/* Default Mode */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Default Mode
        </label>
        <select
          value={config.default_mode}
          onChange={(e) => setConfig({ ...config, default_mode: e.target.value })}
          className="w-full bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
        >
          {(modes || []).map((mode) => (
            <option key={mode.key} value={mode.key}>
              {mode.icon} {mode.displayName}
            </option>
          ))}
        </select>
      </div>

      {/* Default Provider & Model */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Default LLM Provider & Model
        </label>
        <div className="grid grid-cols-2 gap-4">
          <select
            value={config.default_llm_provider}
            onChange={(e) => setConfig({ 
              ...config, 
              default_llm_provider: e.target.value,
              default_llm_model: llmProviders?.find(p => p.provider_key === e.target.value)?.config?.default_model || ""
            })}
            className="bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
          >
            {(llmProviders || []).map((provider) => (
              <option key={provider.provider_key} value={provider.provider_key}>
                {provider.display_name}
              </option>
            ))}
          </select>
          <select
            value={config.default_llm_model}
            onChange={(e) => setConfig({ ...config, default_llm_model: e.target.value })}
            className="bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
          >
            {(llmProviders?.find(p => p.provider_key === config.default_llm_provider)?.config?.models || []).map((model: string) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Fallback Provider */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Fallback Provider (used when primary fails)
        </label>
        <div className="grid grid-cols-2 gap-4">
          <select
            value={config.fallback_llm_provider}
            onChange={(e) => setConfig({ 
              ...config, 
              fallback_llm_provider: e.target.value,
              fallback_llm_model: llmProviders?.find(p => p.provider_key === e.target.value)?.config?.default_model || ""
            })}
            className="bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
          >
            <option value="">No fallback</option>
            {(llmProviders || []).map((provider) => (
              <option key={provider.provider_key} value={provider.provider_key}>
                {provider.display_name}
              </option>
            ))}
          </select>
          {config.fallback_llm_provider && (
            <select
              value={config.fallback_llm_model}
              onChange={(e) => setConfig({ ...config, fallback_llm_model: e.target.value })}
              className="bg-[#0f172a] border border-[#334155] rounded-lg px-4 py-3 text-white focus:border-[#8b3dff] focus:outline-none"
            >
              {(llmProviders?.find(p => p.provider_key === config.fallback_llm_provider)?.config?.models || []).map((model: string) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* User Permissions */}
      <div className="bg-[#1e293b]/50 rounded-xl p-5 border border-[#334155]/50">
        <label className="block text-sm font-medium text-gray-300 mb-4">
          User Permissions
        </label>
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.allow_user_override}
              onChange={(e) => setConfig({ ...config, allow_user_override: e.target.checked })}
              className="w-5 h-5 rounded border-[#334155] bg-[#0f172a] text-[#8b3dff] focus:ring-[#8b3dff] focus:ring-offset-0"
            />
            <div>
              <span className="text-white">Allow users to override defaults</span>
              <p className="text-xs text-gray-500">Users can set their own preferred mode and model</p>
            </div>
          </label>
          
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.allow_user_api_keys}
              onChange={(e) => setConfig({ ...config, allow_user_api_keys: e.target.checked })}
              className="w-5 h-5 rounded border-[#334155] bg-[#0f172a] text-[#8b3dff] focus:ring-[#8b3dff] focus:ring-offset-0"
            />
            <div>
              <span className="text-white">Allow users to use their own API keys</span>
              <p className="text-xs text-gray-500">Users can provide their own OpenAI API key for direct billing</p>
            </div>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-[#334155]/50">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#8b3dff] to-[#6b2fd4] hover:from-[#7a35e6] hover:to-[#5c28bf] text-white rounded-xl font-medium shadow-lg shadow-[#8b3dff]/20 transition-all disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          Save Business Configuration
        </button>
      </div>
    </div>
  );
};

// ===================================
// MAIN SETTINGS PAGE COMPONENT
// ===================================
const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<SettingsTab>("interface");

  // Permission checks
  const canManageAccessCode = user?.role_level !== undefined && user.role_level <= 2;
  const isAdmin = user?.role_level !== undefined && user.role_level <= 2;

  // Tab configuration
  const tabs = [
    {
      key: "interface" as const,
      label: "Interface",
      icon: SettingsIcon,
      description: "Display and UI settings",
      visible: true,
    },
    {
      key: "ai" as const,
      label: "AI Preferences",
      icon: Sparkles,
      description: "Customize AI behavior",
      visible: true,
    },
    {
      key: "access" as const,
      label: "Access Control",
      icon: Shield,
      description: "Manage access codes",
      visible: canManageAccessCode,
    },
    {
      key: "chatbot" as const,
      label: "Chatbot",
      icon: Bot,
      description: "Configure chatbot widgets",
      visible: true,
    },
  ];

  const visibleTabs = tabs.filter((tab) => tab.visible);

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <Header
        title="Enterprise RAG Settings"
        subtitle="Phase 3: RAG-Powered"
      />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-sm text-gray-400 mt-1">
              Manage your preferences and configuration
            </p>
          </div>
          
          {/* User Info Badge */}
          <div className="flex items-center gap-2 px-4 py-2 bg-[#1e293b]/50 rounded-lg border border-[#334155]/50">
            <User className="w-4 h-4 text-[#8b3dff]" />
            <span className="text-sm text-gray-300">{user?.name}</span>
            {isAdmin && (
              <span className="text-xs text-[#8b3dff] bg-[#8b3dff]/10 px-2 py-0.5 rounded">
                Admin
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-[#334155]/50 pb-4 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all whitespace-nowrap ${
                  isActive
                    ? "bg-[#8b3dff]/20 text-white border border-[#8b3dff]/50"
                    : "text-gray-400 hover:text-white hover:bg-[#1e293b]/50 border border-transparent"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-[#8b3dff]" : ""}`} />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="bg-[#0a0f1e]/50 rounded-2xl border border-[#334155]/30 p-6">
          {/* Interface Settings Tab */}
          {activeTab === "interface" && (
            <InterfaceSettings />
          )}

          {/* AI Preferences Tab */}
          {activeTab === "ai" && (
            <div className="space-y-8">
              {/* User Preferences */}
              <AIPreferences />
              
              {/* Business Configuration (Admin Only) */}
              {isAdmin && (
                <div className="pt-8 border-t border-[#334155]/50">
                  <BusinessAIConfiguration />
                </div>
              )}
            </div>
          )}

          {/* Access Control Tab */}
          {activeTab === "access" && canManageAccessCode && (
            <PublicAccessCodeManager />
          )}

          {/* Chatbot Tab */}
          {activeTab === "chatbot" && (
            <CreateChatbot />
          )}
        </div>
      </main>
    </div>
  );
};

export default Settings;